import React, { useCallback, memo, useState, useRef, useEffect } from 'react';
import MenuBarSwiper from '@wconcept/wconcept-ui-renewal/components/atoms/MenuBarSwiper';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { Swiper as SwiperClass } from 'swiper/types';
import FoldMenuBar from '@wconcept/wconcept-ui-renewal/components/organisms/_menuBar/FoldMenuBar';
import WrapMenuBar from '@wconcept/wconcept-ui-renewal/components/organisms/_menuBar/WrapMenuBar';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { BtnFolder } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';

import * as S from './style';

interface IProps {
  id?: string;
  className?: string;
  selectedValue?: number | string | undefined;
  fold?: boolean;
  collection?: boolean;
  type?:
    | 'type1'
    | 'type2'
    | 'type3'
    | 'type4'
    | 'type5'
    | 'type6'
    | 'type7'
    | 'type8' // 기본2
    | 'type9' // 기본3
    | 'type10' // 기본4
    | 'type11' // 기본5
    | 'type12'; // PC 구분선 박스형
  // type1: 기본, type2: square, type3: round, type4: text, type5: underBar, type6: round white, type7: underBar small, type8: 기본2, type9: 기본3, type10: 기본4, type11: 기본5
  bgType?: 'white' | 'black' | undefined;
  showGradient?: boolean; // 그라데이션 표시 여부
  items: React.ReactNode[];
  onClickFold?: () => void;
  onClickFolder?: () => void;
  isDesktop?: boolean;
  keepFirstItemLeftAligned?: boolean;
  /**
   * PC : 스와이프 대신 탭을 여러 줄로 흘리고, 이 줄 수를 넘기면 접어서 "펼치기" 버튼을 노출한다.
   * 미지정이면 지금까지처럼 PC 도 스와이퍼를 쓴다.
   */
  pcFoldLine?: number;
  /**
   * PC : 스와이프 없이 탭을 여러 줄로 흘리기만 한다(접기 없음).
   * pcFoldLine 을 같이 주면 그 줄 수부터 접힌다.
   */
  pcWrap?: boolean;
  /** PC 줄바꿈 모드에서 아이템 좌우 / 줄 간격 */
  pcGap?: { colGap: number; rowGap: number };
}

const CategoryMenuBar = ({
  id,
  className,
  selectedValue,
  items,
  type = 'type2',
  fold = false,
  collection = false,
  bgType = 'white',
  showGradient = false,
  onClickFold,
  onClickFolder,
  isDesktop,
  keepFirstItemLeftAligned = false,
  pcFoldLine,
  pcWrap = false,
  pcGap = { colGap: 8, rowGap: 16 },
}: IProps) => {
  const menuBarRef = useRef<HTMLDivElement | null>(null);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    if (!fold || !swiper) return undefined;

    const measure = () => {
      if (swiper.destroyed) return;
      // 펼친 상태에선 wrapper가 줄바꿈돼 scrollWidth가 컨테이너 폭까지 줄어든다
      if (menuBarRef.current?.classList.contains('unFolding')) return;

      // el.clientWidth는 컨테이너 좌우 padding을 포함하므로 wrapper의 가시 폭으로 잰다
      setIsOverflow(
        swiper.wrapperEl.clientWidth < swiper.wrapperEl.scrollWidth
      );
    };

    measure();
    // 폰트 지연 로드로 경계 길이 항목의 폭이 나중에 확정되는 경우 대응
    document.fonts?.ready.then(measure).catch(() => {});

    window.addEventListener('resize', measure);
    swiper.on('update', measure);
    swiper.on('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);

      if (!swiper.destroyed) {
        swiper.off('update', measure);
        swiper.off('resize', measure);
      }
    };
  }, [fold, swiper]);

  const cName = classNameBind([
    'category-menubar',
    type ? `cm-${type}` : '',
    className || '',
    showGradient && showLeftGradient ? 'is-grdnt-left' : '',
    showGradient && showRightGradient ? 'is-grdnt-right' : '',
  ]);

  // 그라데이션 상태 업데이트 함수
  const updateGradientState = useCallback(
    (swiperInstance: SwiperClass) => {
      if (!showGradient) return;

      const { isBeginning, isEnd } = swiperInstance;

      // 왼쪽 그라데이션: 시작 위치가 아닐 때 표시
      setShowLeftGradient(!isBeginning);

      // 오른쪽 그라데이션: 끝 위치가 아닐 때 표시
      setShowRightGradient(!isEnd);
    },
    [showGradient]
  );

  const handleSwiper = useCallback(
    (swiperInstance: SwiperClass) => {
      setSwiper(swiperInstance);

      // 초기 그라데이션 상태 설정
      if (showGradient) {
        updateGradientState(swiperInstance);
      }

      // Swiper 이벤트 리스너 등록
      if (showGradient) {
        swiperInstance.on('slideChange', () =>
          updateGradientState(swiperInstance)
        );
        swiperInstance.on('progress', () =>
          updateGradientState(swiperInstance)
        );
        swiperInstance.on('setTranslate', () =>
          updateGradientState(swiperInstance)
        );
      }
    },
    [showGradient, updateGradientState]
  );

  // showGradient가 변경될 때 기존 swiper에 이벤트 리스너 추가/제거
  useEffect(() => {
    if (!swiper) return;

    const handleSlideChange = () => updateGradientState(swiper);
    const handleProgress = () => updateGradientState(swiper);
    const handleSetTranslate = () => updateGradientState(swiper);

    if (showGradient) {
      // 이벤트 리스너 등록
      swiper.on('slideChange', handleSlideChange);
      swiper.on('progress', handleProgress);
      swiper.on('setTranslate', handleSetTranslate);

      // 초기 상태 설정
      updateGradientState(swiper);
    } else {
      // 그라데이션 비활성화 시 상태 초기화
      setShowLeftGradient(false);
      setShowRightGradient(false);
    }

    // cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (swiper && !swiper.destroyed) {
        swiper.off('slideChange', handleSlideChange);
        swiper.off('progress', handleProgress);
        swiper.off('setTranslate', handleSetTranslate);
      }
    };
  }, [swiper, showGradient, updateGradientState]);

  const renderMenuBarSwiper = useCallback(
    () => (
      <>
        {collection && (
          <S.Left className="area-left">
            <Button
              label="컬렉션페이지 이동"
              onClick={onClickFolder}
              icon={<BtnFolder />}
            />
          </S.Left>
        )}
        <MenuBarSwiper
          ref={menuBarRef}
          items={items}
          selectedValue={selectedValue}
          speed={300}
          className={
            fold && (isDesktop ? isOverflow : items?.length >= 4)
              ? 'category-fold'
              : ''
          }
          resistanceRatio={isDesktop ? 0 : 0.85}
          isDesktop={isDesktop}
          keepFirstItemLeftAligned={keepFirstItemLeftAligned}
          autoUpdate
          onSwiper={handleSwiper}
        />
      </>
    ),
    [
      collection,
      onClickFolder,
      items,
      selectedValue,
      isDesktop,
      fold,
      isOverflow,
      keepFirstItemLeftAligned,
      handleSwiper,
    ]
  );

  // PC 는 스와이프가 없는 디자인이면 줄바꿈 리스트로 그린다 (탭 스킨은 cm-type* 이 그대로 잡는다)
  if (isDesktop && (pcWrap || pcFoldLine)) {
    return (
      <S.CategoryMenuBar id={id} className={cName} type={type} $bgType={bgType}>
        <WrapMenuBar
          foldLine={pcFoldLine}
          bgType={bgType}
          colGap={pcGap.colGap}
          rowGap={pcGap.rowGap}
        >
          {/* 선택 상태(active)는 아이템이 selectedValue 를 받아야 붙는다 (MenuBarSwiper 와 동일) */}
          {React.Children.map(items, child =>
            React.isValidElement(child)
              ? React.cloneElement(
                  child as React.ReactElement<{
                    selectedValue?: number | string;
                  }>,
                  { selectedValue }
                )
              : child
          )}
        </WrapMenuBar>
      </S.CategoryMenuBar>
    );
  }

  return (
    <S.CategoryMenuBar id={id} className={cName} type={type} $bgType={bgType}>
      {!fold ? (
        renderMenuBarSwiper()
      ) : (
        <FoldMenuBar
          onClickFold={onClickFold}
          type={type}
          itemsLength={items?.length}
          isOverflow={isOverflow}
          menuBarOnSwiper={swiper}
          childrenMenuBarRef={menuBarRef}
          isDesktop={isDesktop}
        >
          {renderMenuBarSwiper()}
        </FoldMenuBar>
      )}
    </S.CategoryMenuBar>
  );
};

export default memo(CategoryMenuBar);
